import React, {useMemo} from "react";
import {Col, Label, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import {useFormik} from "formik";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import * as Yup from "yup";
import {UserService} from "../../../../services/user.service";
import {TrafficOrderService} from "../../../../services/traffic_order.service";
import Select from "../../../../components/Common/Select";

interface IAssignOrder {
    isOpen: boolean;
    toggle: () => void;
    id: string;
}

interface AssignOrderData {
    buyer_ids: number[];
}

const createSchema = Yup.object({
    buyer_ids: Yup.array().min(1, 'At least one buyer must be selected'),
});

const updateSchema = Yup.object({
    buyer_ids: Yup.array(),
});

const AssignOrder: React.FC<IAssignOrder> = ({isOpen, toggle, id}) => {
    const queryClient = useQueryClient();

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyTrafficOrders"]})
            .then(() => toggle())
    }

    const {data: usersBuyer, isLoading: isLoadingUsers} = useQuery({
        queryKey: ["keyUsersBuyerTeamLead"],
        queryFn: UserService.getUsersBuyerTeamLead
    });

    const {data: usersResponsible, isFetching: isLoadingResponsibility} = useQuery({
        queryKey: ["keyResponsibilityTrafficOrder", id],
        queryFn: () => TrafficOrderService.getResponsibilityTrafficOrder(+id)
    });

    const {mutate: assignTrafficOrder, isPending} = useMutation({
        mutationFn: (data: AssignOrderData) => TrafficOrderService.assignTrafficOrder(data, +id),
        mutationKey: ["keyAssignTrafficOrder", id],
        onSuccess: onSuccess
    });

    const initialValues: AssignOrderData = useMemo(() => ({
        buyer_ids: usersResponsible?.map((i: any) => i.value) || [],
    }), [usersResponsible]);

    const handleSubmitForm = (values: AssignOrderData) => {
        assignTrafficOrder(values);
    }

    const isUpdateModal = usersResponsible?.length !== 0;
    const validationSchema = isUpdateModal ? updateSchema : createSchema

    const {
        values,
        handleSubmit,
        isValid,
        setFieldValue,
        resetForm,
        errors,
        touched
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const handleChangeMulti = (name: string, value: any) => {
        const multiIds = value ? value.map((item: any) => item.value) : []
        setFieldValue(name, multiIds)
    }

    const selectedValue = useMemo(() =>
            usersBuyer?.filter((u: any) => values.buyer_ids.includes(u.value)) || null
        , [values.buyer_ids, usersBuyer]);

    const closeModal = () => {
        toggle();
        resetForm();
    };

    const isLoading = isLoadingUsers || isLoadingResponsibility;
    const isSaving = isPending || !!queryClient.isFetching({queryKey: ["keyTrafficOrders"]});

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal} tag="h4">
                Assign Order {id}
            </ModalHeader>
            <ModalBody>
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <div className="mb-3">
                                <Label>Select responsible buyers: </Label>
                                <Select
                                    isMulti
                                    isSearchable
                                    isClearable
                                    name="buyer_ids"
                                    label="Buyers"
                                    placeholder="Select buyers"
                                    isLoading={isLoading}
                                    isDisabled={isLoading || isSaving}
                                    options={usersBuyer}
                                    value={!isLoading && selectedValue}
                                    onChange={(value: any) => handleChangeMulti("buyer_ids", value)}
                                    closeMenuOnSelect={false}
                                />
                                {!!(errors.buyer_ids && touched.buyer_ids) && (
                                    <div className="invalid-feedback d-block m-0">{errors.buyer_ids}</div>)}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="text-end">
                            <button type="submit" className="btn btn-success"
                                    disabled={!isValid || isLoading || isSaving}>
                                {isSaving ? "Loading..." : "Save"}
                            </button>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
        </Modal>
    );
};

export default AssignOrder;